import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { STALE_TIME_MS } from "../../../hooks/constants";
import { useAllExperiences } from "../../../hooks/useAllExperiences";

interface UseUserGroupExperiencesOptions {
  userGroupId?: string;
  enabled?: boolean;
}

interface UseUserGroupExperiencesResult {
  experiences: UserGroupExperienceRow[];
  isLoading: boolean;
  error: Error | null;
}

export interface UserGroupExperienceRow {
  id: string;
  title: string;
  companyName?: string;
  expirationDate?: Date;
  contentMetadataId: string;
}

const mapRCToExperienceRow = (
  rc: UserManagement_V1.ReleaseChannel,
  title: string,
  contentMetadataId: string,
  companyName?: string,
): UserGroupExperienceRow => ({
  id: rc.releaseChannelId,
  title,
  companyName,
  expirationDate: rc.expiresAt,
  contentMetadataId,
});

export const USER_GROUP_EXPERIENCES_QUERY_KEY = "userGroupExperiences";

export const useUserGroupExperiences = (
  options: UseUserGroupExperiencesOptions,
): UseUserGroupExperiencesResult => {
  const { userGroupId, enabled = true } = options;

  const { experiencesMap, isLoading: isAllExperiencesLoading } =
    useAllExperiences();

  const userManagementClient = useUserManagementServiceV1();

  const {
    data,
    isLoading: isGroupExperiencesLoading,
    error,
  } = useQuery({
    queryKey: [USER_GROUP_EXPERIENCES_QUERY_KEY, userGroupId],
    queryFn: async () => {
      if (!userGroupId) throw new Error("User group ID is required");

      return await userManagementClient.getReleaseChannelsForUserGroup(
        userGroupId,
      );
    },
    enabled: enabled && !!userGroupId,
    staleTime: STALE_TIME_MS,
  });

  const experiences = useMemo(() => {
    if (!data) return [];

    return data
      .map((rc) => {
        const experience = experiencesMap.get(rc.releaseChannelId);
        if (!experience) return undefined;

        return mapRCToExperienceRow(
          rc,
          experience.friendlyName,
          experience.contentMetadataId,
          experience.companyName,
        );
      })
      .filter((item): item is UserGroupExperienceRow => item !== undefined);
  }, [data, experiencesMap]);

  const isLoading = isGroupExperiencesLoading || isAllExperiencesLoading;

  return useMemo(
    () => ({
      experiences,
      isLoading,
      error,
    }),
    [experiences, isLoading, error],
  );
};
